import { Settings } from './settings.interface';
import { Injectable } from '@angular/core';
import { BcRestApi } from 'common/api/rest-api';

interface ServiceFeatureChecker {
  isEnabled(settingsObj: Settings, featureKey: string): boolean;
}

@Injectable()
export class BcSettingsService {
  public ChatFeatures: ServiceFeatureChecker;
  public SmsFeatures: ServiceFeatureChecker;
  public FacebookFeatures: ServiceFeatureChecker;
  public AsyncMessageFeatures: ServiceFeatureChecker;
  public EmailFeatures: ServiceFeatureChecker;
  public VisitFeatures: ServiceFeatureChecker;
  private _settings: Settings;
  private _remappedSettings;

  constructor(private _bcRestApi: BcRestApi) {
    this.resetSettings();

    this.ChatFeatures = {
      isEnabled: (settingsObj: Settings, featureKey: string): boolean => {
        return BcSettingsService._isFeatureEnabled(featureKey, settingsObj.chat.featureSettings);
      }
    };

    this.SmsFeatures = {
      isEnabled: (settingsObj: Settings, featureKey: string): boolean => {
        return BcSettingsService._isFeatureEnabled(featureKey, settingsObj.textChat.sms.featureSettings);
      }
    };

    this.FacebookFeatures = {
      isEnabled: (settingsObj: Settings, featureKey: string): boolean => {
        return BcSettingsService._isFeatureEnabled(featureKey, settingsObj.textChat.facebook.featureSettings);
      }
    };

    this.AsyncMessageFeatures = {
      isEnabled: (settingsObj: Settings, featureKey: string): boolean => {
        return BcSettingsService._isFeatureEnabled(featureKey, settingsObj.textChat.asyncMessage.featureSettings);
      }
    };

    this.EmailFeatures = {
      isEnabled: (settingsObj: Settings, featureKey: string): boolean => {
        return BcSettingsService._isFeatureEnabled(featureKey, settingsObj.email.featureSettings);
      }
    };

    this.VisitFeatures = {
      isEnabled: (settingsObj: Settings, featureKey: string): boolean => {
        return BcSettingsService._isFeatureEnabled(featureKey, settingsObj.visit.featureSettings);
      }
    };
  }

  public resetSettings(): void {
    this._settings = null;
    this._remappedSettings = null;
  }

  public getSettings(): Promise<Settings> {
    if (this._settings) {
      return Promise.resolve(this._settings);
    }

    return this._bcRestApi.getSettings().then((settings) => {
      this._settings = settings;
      return this._settings;
    });
  }

  public getInactivityTimeout(): Promise<number> {
    return this.getSettings().then((settings: Settings) => {
      return settings.accountGeneralSettings && settings.accountGeneralSettings.autoLogoutTimeoutInMinutes
        ? settings.accountGeneralSettings.autoLogoutTimeoutInMinutes
        : null;
    });
  }

  // Returns a settings key/value pair from the settings object for the Visitor Info Card
  // (which is shared between emails and chats).
  public getRemapSettingsForVisitorInfoCard(settings: Settings) {
    if (this._remappedSettings) {
      return this._remappedSettings;
    }

    const remappedSettings = {};

    if (settings) {
      this._addRemappedSettingsForServiceType(remappedSettings, settings.chat, 'chat');
      this._addRemappedSettingsForServiceType(remappedSettings, settings.email, 'email');
      this._addRemappedSettingsForServiceType(remappedSettings, settings.visit, 'visit');
      this._addRemappedSettingsForServiceType(remappedSettings, settings.textChat.facebook, 'facebook');
      this._addRemappedSettingsForServiceType(remappedSettings, settings.textChat.asyncMessage, 'asyncMessage');
      this._addRemappedSettingsForServiceType(remappedSettings, settings.textChat.sms, 'sms');
    }

    this._remappedSettings = remappedSettings;
    return this._remappedSettings;
  }

  private static _isFeatureEnabled(featureKey: string, featureSettings): boolean {
    return featureSettings[featureKey] === 1;
  }

  private _addRemappedSettingsForServiceType(remappedSettings, settingsContext, serviceType): void {
    if (settingsContext && settingsContext.visitorInfoCard) {
      const fieldSettings = settingsContext.visitorInfoCard;
      for (let i = 0; i < fieldSettings.length; i++) {
        const fieldSetting = fieldSettings[i];
        remappedSettings['settings.' + serviceType + '.visitorInfoCard.' + fieldSetting.keyName] = {
          visibility: fieldSetting.visibility
        };
      }
    }
  }
}
